import { pDecimalValidationTest } from '@/components/mixins/ValidatedInput.js';
import { email, max, min_value, integer } from '@vee-validate/rules';

export default {
    data() {
        return {
            supplierConfiguration: {
                type: 'suppliers',
                label: 'Suppliers',
                importModes: [
                    {value:'add', text:'Add New (Skip when supplier code already present)'},
                    {value:'update', text:'Update (Skip when supplier code not present, update when found)'},
                    {value:'addAndUpdate', text:'Add and Update'}
                ],
                sampleNotes: [
                    'Any Code fields left blank will be generated',
                    'State must be provided as a valid abbreviation or the full state name',
                    'If ReturnAddressSameAsOrdering is Yes, any data in the return address fields will be ignored and the ordering address data will be used',                    
                    'Supplier code is required to match existing suppliers'
                ],
                fields: [
                    {
                        key: 'Name',
                        label: 'Name',
                        canConvertCase: true,
                        hasErrorCalculator: value => {
                            return (
                                value === '' ||
                                max(value, { length: 50 }) !== true
                            );
                        },
                        rules: 'required|max:50',
                        samples: ['"AAA Supplier, Inc."','"ABC Supplier, Inc."','"Paper Providers"']
                    },
                    {
                        key: 'Alias',
                        label: 'Alias',
                        rules: 'max:10',
                        hasWarningCalculator: value => {
                            return max(value, { length: 10 }) !== true;
                        },
                        warningDefault: '',
                        samples: ['"AAASup"','"ABCSup"','"PPaper"']
                    },
                    {
                        key: 'Code',
                        label: 'Code',
                        hasWarningCalculator: (value) => {
                            return max(value, { length: 10 }) !== true;
                        },
                        warningDefault: null,
                        rules: {
                            max: { length: 10 }
                        },
                        samples: ['"AAA001"','"ABC001"','"PAP001"']
                    },
                    {
                        key: 'Email',
                        label: 'Email',
                        hasWarningCalculator: value => {
                            if (value === '' || value === undefined) {
                                return false;
                            }
                            return (
                                email(value) !== true ||
                                max(value, { length: 254 }) !== true
                            );
                        },
                        warningDefault: '',
                        rules: 'email|max:254',
                        samples: ['"AAASupplier@supplies.com"','"ABCSupplier@supplies.com"','"paper@supplies.com"']
                    },
                    {
                        key: 'Url',
                        label: 'URL',
                        hasWarningCalculator: value => {
                            return max(value, { length: 255 }) !== true;
                        },
                        warningDefault: '',
                        rules: 'max:255',
                        samples: ['"www.AAASupplierInc.com"','"www.ABVSupplierInc.com"','"www.PaperProvider.com"']
                    },
                    // Ordering Address
                    {
                        key: 'OrderingAddress',
                        label: 'Ordering Address',
                        hasWarningCalculator: value => {
                            return max(value, { length: 255 }) !== true;
                        },
                        warningDefault: '',
                        rules: 'max:255',
                        samples: ['"223 N 1st Street"','"1487 N 2nd Street"','"1212 S 1st Street"']
                    },
                    {
                        key: 'OrderingCity',
                        label: 'Ordering City',
                        hasWarningCalculator: value => {
                            return max(value, { length: 50 }) !== true;
                        },
                        warningDefault: '',
                        rules: 'max:50',
                        samples: ['"Vernon"','"Phoenix"','"Omaha"']
                    },
                    {
                        key: 'OrderingState',
                        label: 'Ordering State',
                        dataType: 'states',
                        comparisonProperty: ['abbreviation', 'text'],
                        hasWarningCalculator: value => {
                            return (
                                value != '' &&
                                value != undefined &&
                                min_value(value, { min: 0 }) !== true
                            );
                        },
                        warningDefault: null,
                        rules: 'min_value:0',
                        samples: ['"CA"','"AZ"','"NE"']
                    },
                    {
                        key: 'OrderingPostalCode',
                        label: 'Ordering Postal Code',
                        hasWarningCalculator: value => {
                            return max(value, { length: 20 }) !== true;
                        },
                        warningDefault: '',
                        rules: 'max:20',
                        samples: ['"90058"','"87516"','"12491"']
                    },
                    {
                        key: 'OrderingPhoneNumber',
                        label: 'Ordering Phone Number',
                        hasWarningCalculator: value => {
                            return max(value, { length: 20 }) !== true;
                        },
                        warningDefault: '',
                        rules: 'max:20',
                        samples: ['"1-555-444-3333"','"1-555-789-3333"','"1-555-845-3333"']
                    },
                    {
                        key: 'OrderingFaxNumber',
                        label: 'Ordering Fax Number',
                        hasWarningCalculator: value => {
                            return max(value, { length: 20 }) !== true;
                        },
                        warningDefault: '',
                        rules: 'max:20',
                        samples: ['"1-555-444-3334"','"1-555-789-3334"','"1-555-845-4215"']
                    },
                    // Returns Address
                    {
                        key: 'ReturnAddressSameAsOrdering',
                        label: 'Return Address Same as Ordering',
                        hasWarningCalculator: value => {
                            return !this.matchesExistingObject(value);
                        },
                        warningDefault: false,
                        dataType: 'yesNoOptions',
                        rules: 'min_value:0',
                        samples: ['"Yes"', '"No"', '""']
                    },
                    {
                        key: 'ReturnsAddress',
                        label: 'Returns Address',
                        hasWarningCalculator: value => {
                            return max(value, { length: 255 }) !== true;
                        },
                        warningDefault: '',
                        rules: 'max:255',
                        samples: ['"223 N 1st Street"','"1487 N 2nd Street"','"1212 S 1st Street"']
                    },
                    {
                        key: 'ReturnsCity',
                        label: 'Returns City',
                        hasWarningCalculator: value => {
                            return max(value, { length: 50 }) !== true;
                        },
                        warningDefault: '',
                        rules: 'max:50',
                        samples: ['"Vernon"','"Phoenix"','"Omaha"']
                    },

                    {
                        key: 'ReturnsState',
                        label: 'Returns State',
                        dataType: 'states',
                        comparisonProperty: ['abbreviation', 'text'],
                        hasWarningCalculator: value => {
                            return (
                                value != '' &&
                                value != undefined &&
                                min_value(value, { min: 0 }) !== true
                            );
                        },
                        warningDefault: null,
                        rules: 'min_value:0',
                        samples: ['"CA"','"AZ"','"NE"']
                    },
                    {
                        key: 'ReturnsPostalCode',
                        label: 'Returns Postal Code',
                        hasWarningCalculator: value => {
                            return max(value, { length: 20 }) !== true;
                        },
                        warningDefault: '',
                        rules: 'max:20',
                        samples: ['"90058"','"87516"','"12491"']
                    },
                    {
                        key: 'ReturnsPhoneNumber',
                        label: 'Returns Phone Number',
                        hasWarningCalculator: value => {
                            return max(value, { length: 20 }) !== true;
                        },
                        warningDefault: '',
                        rules: 'max:20',
                        samples: ['"1-555-444-3333"','"1-555-789-3333"','"1-555-845-3333"']
                    },
                    {
                        key: 'ReturnsFaxNumber',
                        label: 'Returns Fax Number',
                        hasWarningCalculator: value => {
                            return max(value, { length: 20 }) !== true;
                        },
                        warningDefault: '',
                        rules: 'max:20',
                        samples: ['"1-555-444-3334"','"1-555-789-3334"','"1-555-845-4215"']
                    },
                    {
                        key: 'EasyLink',
                        label: 'Easy Link',
                        hasWarningCalculator: value => {
                            return max(value, { length: 20 }) !== true;
                        },
                        warningDefault: '',
                        rules: 'max:20',
                        samples: ['"EZLA"','"EZLB"','"EZLC"']
                    },
                    {
                        key: 'PubnetSan',
                        label: 'Pubnet San',
                        hasErrorCalculator: value => {
                            if (value === '' || value === undefined)
                                return false;
                            
                            if (!integer(value))
                                return true;

                            return max(value, { length: 7 }) !== true;
                        },
                        warningDefault: '',
                        rules: 'max:7',
                        samples: ['"0124660"','"8571584"','"2014568"']
                    },
                    {
                        key: 'AccountNumberWithSupplier',
                        label: 'Account Number with Supplier',
                        hasWarningCalculator: value => {
                            return (
                                max(value, { length: 15 }) !== true
                            );
                        },
                        warningDefault: '',
                        rules: 'max:15',
                        samples: ['"5367"','"4567"','"78957"']
                    },
                    {
                        key: 'FreeShippingOrderValue',
                        label: 'Free Shipping Order Value',
                        hasWarningCalculator: value => {
                            return (
                                value &&
                                (min_value(value, { min: 0 }) !== true ||
                                    !pDecimalValidationTest(value, {
                                        precision: '16',
                                        scale: '2'
                                    }))
                            );
                        },
                        warningDefault: null,
                        convertToNumber: true,
                        rules: 'p-decimal:16,2|min_value:0',
                        samples: [50.25,35,100]
                    },
                    {
                        key: 'ShippingProvider',
                        label: 'Shipping Provider',
                        dataType: 'shippingProviders',
                        hasWarningCalculator: value => {
                            return (
                                value != '' &&
                                value != undefined &&
                                min_value(value, { min: 0 }) !== true
                            );
                        },
                        //using -1 to indicate that we received an invalid value and not an empty value
                        warningDefault: -1,
                        rules: 'min_value:0',
                        samples: ['"Best Method"','""','""']
                    },
                    {
                        key: 'ShippingLevel',
                        label: 'Shipping Level',
                        dataType: 'shippingLevels',
                        hasWarningCalculator: value => {
                            return (
                                value != '' &&
                                value != undefined &&
                                min_value(value, { min: 0 }) !== true
                            );
                        },
                        warningDefault: null,
                        rules: 'min_value:0',
                        samples: ['"Overnight"','"Second Day"','""']
                    },
                    {
                        key: 'ReturnPolicy',
                        label: 'Return Policy',
                        hasWarningCalculator: (value) => {
                            return !this.matchesExistingObject(value);
                        },
                        warningDefault: null,
                        convertToNumber: true,
                        rules: 'min_value:0',
                        samples: ['Return permission required', 'No permission required', 'No returns'],
                        dataType: 'supplierReturnPolicies'
                    },
                    {
                        key: 'ReturnMinDays',
                        label: 'Return Min Days',
                        hasWarningCalculator: (value) => {
                            return !this.isNonNegativeInteger(value);
                        },
                        warningDefault: null,
                        convertToNumber: true,
                        rules: 'integer|min_value:0',
                        samples: ['30', '5', '10']
                    },
                    {
                        key: 'ReturnMaxDays',
                        label: 'Return Max Days',
                        hasWarningCalculator: (value) => {
                            return !this.isNonNegativeInteger(value);
                        },
                        warningDefault: null,
                        convertToNumber: true,
                        rules: 'integer|min_value:0',
                        samples: ['90', '30', '30']
                    },
                    {
                        key: 'ReturnComment',
                        label: 'Return Comment',
                        hasErrorCalculator: value => {
                            return max(value, { length: 255 }) !== true;
                        },
                        rules: {
                            max: {length: 255 }
                        },
                        samples: ['"Return Comment1"','"Always use orange box"','"Do not overfill boxes"','""']
                    }
                ]
            }
        };
    }
};
